

export type Sex = 'Male' | 'Female'

export interface Allergy {
  substance: string
  category: 'Drug' | 'Food' | 'Environmental' | 'Other'
  severity: 'Mild' | 'Moderate' | 'Severe' | 'Anaphylaxis'
  reaction: string
}

export interface Encounter {
  id: string
  type: 'ER' | 'OPD' | 'IPD' | 'Daycare' | 'Video consultation'
  date: string
  department: string
  provider: string
  diagnosis: string
  icd10: string
  status: 'Active' | 'Closed'
}

export interface Vital {
  label: string
  value: string
  unit: string
  trend: 'up' | 'down' | 'flat'
  flag?: 'high' | 'low' | 'critical'
}

export interface Patient {
  id: string
  mrn: string
  name: string
  age: number
  sex: Sex
  dob: string
  bloodGroup: string
  nationalId: string
  phone: string
  payer: string
  photo?: string
  status: 'Admitted' | 'Outpatient' | 'Discharged' | 'Emergency'
  location?: string
  allergies: Allergy[]
  chronic: string[]
  encounters: Encounter[]
  vitals: Vital[]
  alerts: string[]
}

export const patients: Patient[] = [
  {
    id: 'p1',
    mrn: 'MRN-0048213',
    name: 'Ayesha Rahman',
    age: 54,
    sex: 'Female',
    dob: '1971-03-12',
    bloodGroup: 'O+',
    nationalId: '35202-1145782-6',
    phone: '+92 300 1145782',
    payer: 'State Life Insurance',
    photo: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop',
    status: 'Admitted',
    location: 'Cardiology Ward · Room 412 · Bed 2',
    allergies: [
      { substance: 'Penicillin', category: 'Drug', severity: 'Anaphylaxis', reaction: 'Airway swelling, hives' },
      { substance: 'Sulfa drugs', category: 'Drug', severity: 'Severe', reaction: 'Stevens-Johnson' },
    ],
    chronic: ['Type 2 Diabetes Mellitus', 'Hypertension', 'CAD'],
    alerts: ['Fall risk (Morse 65)', 'Anticoagulated'],
    encounters: [
      { id: 'e1', type: 'IPD', date: '2026-07-16', department: 'Cardiology', provider: 'Dr. Imran Sethi', diagnosis: 'NSTEMI', icd10: 'I21.4', status: 'Active' },
      { id: 'e2', type: 'OPD', date: '2026-06-02', department: 'Cardiology', provider: 'Dr. Imran Sethi', diagnosis: 'Angina pectoris', icd10: 'I20.9', status: 'Closed' },
      { id: 'e3', type: 'ER', date: '2026-03-19', department: 'Emergency', provider: 'Dr. Sana Malik', diagnosis: 'Chest pain, unspecified', icd10: 'R07.9', status: 'Closed' },
    ],
    vitals: [
      { label: 'Heart Rate', value: '96', unit: 'bpm', trend: 'up', flag: 'high' },
      { label: 'Blood Pressure', value: '148/92', unit: 'mmHg', trend: 'up', flag: 'high' },
      { label: 'SpO₂', value: '97', unit: '%', trend: 'flat' },
      { label: 'Temp', value: '37.1', unit: '°C', trend: 'flat' },
      { label: 'Resp Rate', value: '18', unit: '/min', trend: 'flat' },
      { label: 'Blood Glucose', value: '212', unit: 'mg/dL', trend: 'up', flag: 'high' },
    ],
  },
  {
    id: 'p2',
    mrn: 'MRN-0051902',
    name: 'Bilal Ahmed Khan',
    age: 8,
    sex: 'Male',
    dob: '2018-01-30',
    bloodGroup: 'A+',
    nationalId: '—',
    phone: '+92 321 8890112',
    payer: 'Self-pay',
    status: 'Emergency',
    location: 'Emergency · Bay R-2 (Resus)',
    allergies: [],
    chronic: ['Asthma'],
    alerts: ['Pediatric', 'Guardian: mother present'],
    encounters: [
      { id: 'e4', type: 'ER', date: '2026-07-19', department: 'Emergency', provider: 'Dr. Sana Malik', diagnosis: 'Acute asthma exacerbation', icd10: 'J45.901', status: 'Active' },
    ],
    vitals: [
      { label: 'Heart Rate', value: '138', unit: 'bpm', trend: 'up', flag: 'critical' },
      { label: 'Blood Pressure', value: '102/64', unit: 'mmHg', trend: 'flat' },
      { label: 'SpO₂', value: '89', unit: '%', trend: 'down', flag: 'critical' },
      { label: 'Temp', value: '37.8', unit: '°C', trend: 'up' },
      { label: 'Resp Rate', value: '34', unit: '/min', trend: 'up', flag: 'high' },
    ],
  },
  {
    id: 'p3',
    mrn: 'MRN-0043019',
    name: 'Fatima Zafar',
    age: 29,
    sex: 'Female',
    dob: '1996-11-08',
    bloodGroup: 'B+',
    nationalId: '42101-7788990-2',
    phone: '+92 333 4455667',
    payer: 'Corporate — Systems Ltd',
    photo: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=200&h=200&fit=crop',
    status: 'Outpatient',
    allergies: [{ substance: 'Latex', category: 'Environmental', severity: 'Moderate', reaction: 'Contact dermatitis' }],
    chronic: ['Hypothyroidism'],
    alerts: ['Pregnant — G2P1, 24 weeks'],
    encounters: [
      { id: 'e5', type: 'OPD', date: '2026-07-18', department: 'Obstetrics', provider: 'Dr. Nadia Qureshi', diagnosis: 'Antenatal supervision', icd10: 'Z34.83', status: 'Active' },
    ],
    vitals: [
      { label: 'Heart Rate', value: '84', unit: 'bpm', trend: 'flat' },
      { label: 'Blood Pressure', value: '118/74', unit: 'mmHg', trend: 'flat' },
      { label: 'SpO₂', value: '99', unit: '%', trend: 'flat' },
      { label: 'Temp', value: '36.8', unit: '°C', trend: 'flat' },
    ],
  },
  {
    id: 'p4',
    mrn: 'MRN-0039875',
    name: 'Muhammad Usman',
    age: 67,
    sex: 'Male',
    dob: '1958-09-21',
    bloodGroup: 'AB+',
    nationalId: '35201-9988776-1',
    phone: '+92 302 1122334',
    payer: 'Sehat Sahulat (Govt)',
    status: 'Admitted',
    location: 'ICU · Bed 3',
    allergies: [{ substance: 'Contrast dye (iodine)', category: 'Drug', severity: 'Severe', reaction: 'Anaphylactoid' }],
    chronic: ['COPD', 'CKD Stage 3', 'AFib'],
    alerts: ['DNR — documented', 'Critical labs pending'],
    encounters: [
      { id: 'e6', type: 'IPD', date: '2026-07-14', department: 'Pulmonology', provider: 'Dr. Kamran Ali', diagnosis: 'Acute respiratory failure', icd10: 'J96.00', status: 'Active' },
    ],
    vitals: [
      { label: 'Heart Rate', value: '112', unit: 'bpm', trend: 'up', flag: 'high' },
      { label: 'Blood Pressure', value: '96/58', unit: 'mmHg', trend: 'down', flag: 'low' },
      { label: 'SpO₂', value: '91', unit: '%', trend: 'down', flag: 'low' },
      { label: 'Temp', value: '38.4', unit: '°C', trend: 'up', flag: 'high' },
      { label: 'Resp Rate', value: '26', unit: '/min', trend: 'up', flag: 'high' },
    ],
  },
  {
    id: 'p5',
    mrn: 'MRN-0055120',
    name: 'Zara Sheikh',
    age: 41,
    sex: 'Female',
    dob: '1985-05-17',
    bloodGroup: 'O-',
    nationalId: '35202-3344556-8',
    phone: '+92 345 6677889',
    payer: 'State Life Insurance',
    status: 'Outpatient',
    allergies: [],
    chronic: [],
    alerts: [],
    encounters: [
      { id: 'e7', type: 'OPD', date: '2026-07-19', department: 'Dermatology', provider: 'Dr. Hassan Raza', diagnosis: 'Follow-up', icd10: 'Z09', status: 'Active' },
    ],
    vitals: [
      { label: 'Heart Rate', value: '72', unit: 'bpm', trend: 'flat' },
      { label: 'Blood Pressure', value: '120/80', unit: 'mmHg', trend: 'flat' },
      { label: 'SpO₂', value: '99', unit: '%', trend: 'flat' },
      { label: 'Temp', value: '36.6', unit: '°C', trend: 'flat' },
    ],
  },
]

export function getPatient(id: string) {
  return patients.find((p) => p.id === id)
}


